<script>
    import { onMount } from 'svelte';
    import { t } from 'svelte-i18n';
    import { fetchCategoriasDisponibles, fetchProductos } from '../controllers/productoController.js';
    import { fetchCategorias } from '../controllers/categoriaController.js';

    // State
    let categorias = [];
    let productos = [];
    let productosFiltrados = [];
    let categoriaSeleccionada = '';
    let busqueda = '';
    let cargando = false;

    // Cargar categorías al montar el componente
    onMount(async () => {
        await cargarCategorias();
        await cargarProductos();
    });

    async function cargarCategorias() {
        try {
            const response = await fetchCategorias(1, 1000);
            if (response && response.categoria) {
                categorias = response.categoria;
            }
        } catch (error) {
            console.error('Error cargando categorías:', error);
        }
    }

    async function cargarProductos() {
        try {
            cargando = true;
            const response = await fetchProductos(1, 1000);
            if (response && response.producto) {
                productos = response.producto;
                productosFiltrados = response.producto;
            }
        } catch (error) {
            console.error('Error cargando productos:', error);
        } finally {
            cargando = false;
        }
    }

    function filtrarProductos() {
        let filtrados = [...productos];

        // Filtrar por categoría
        if (categoriaSeleccionada) {
            filtrados = filtrados.filter(producto => 
                producto.categorias && producto.categorias.some(cat => 
                    cat.categoria && cat.categoria.id === categoriaSeleccionada
                )
            );
        }

        // Filtrar por búsqueda
        if (busqueda.trim()) {
            const termino = busqueda.toLowerCase();
            filtrados = filtrados.filter(producto => 
                producto.nombre.toLowerCase().includes(termino) ||
                producto.descrip.toLowerCase().includes(termino)
            );
        }

        productosFiltrados = filtrados;
    }

    function limpiarFiltros() {
        categoriaSeleccionada = '';
        busqueda = '';
        productosFiltrados = [...productos];
    }

    function obtenerNombreCategoria(categoriaId) {
        const categoria = categorias.find(cat => cat.id === categoriaId);
        return categoria ? categoria.descrip : 'Categoría no encontrada';
    }

    function obtenerCategoriasProducto(producto) {
        if (!producto.categorias || producto.categorias.length === 0) {
            return 'Sin categorías';
        }
        return producto.categorias.map(cat => cat.categoria.descrip).join(', ');
    }
</script>

<div class="container mx-auto px-4 py-8">
    <div class="bg-white rounded-lg shadow-lg p-6">
        <h1 class="text-3xl font-bold text-gray-800 mb-6">{$t('categories.categoryFilter')}</h1>
        
        <!-- Filtros -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
                <label for="categoria" class="block text-sm font-medium text-gray-700 mb-2">
                    {$t('categories.filterByCategory')}
                </label>
                <select 
                    id="categoria"
                    bind:value={categoriaSeleccionada}
                    on:change={filtrarProductos}
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">{$t('categories.allCategories')}</option>
                    {#each categorias as categoria}
                        <option value={categoria.id}>{categoria.descrip}</option>
                    {/each}
                </select>
            </div>
            
            <div>
                <label for="busqueda" class="block text-sm font-medium text-gray-700 mb-2">
                    {$t('categories.searchProduct')}
                </label>
                <input 
                    id="busqueda"
                    type="text"
                    bind:value={busqueda}
                    on:input={filtrarProductos}
                    placeholder={$t('categories.nameOrDescription')}
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>
            
            <div class="flex items-end">
                <button 
                    on:click={limpiarFiltros}
                    class="w-full bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition duration-200"
                >
                    {$t('categories.clearFilters')}
                </button>
            </div>
        </div>

        <!-- Resultados -->
        <div class="mb-4">
            <p class="text-sm text-gray-600">
                {$t('categories.showingProducts', { count: productosFiltrados.length, total: productos.length })}
            </p>
        </div>

        <!-- Lista de Productos -->
        {#if cargando}
            <div class="flex justify-center items-center py-8">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span class="ml-2 text-gray-600">{$t('categories.loadingProducts')}</span>
            </div>
        {:else if productosFiltrados.length === 0}
            <div class="text-center py-8">
                <p class="text-gray-500 text-lg">{$t('categories.noProductsFound')}</p>
            </div>
        {:else}
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {#each productosFiltrados as producto}
                    <div class="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition duration-200">
                        <div class="flex justify-between items-start mb-2">
                            <h3 class="text-lg font-semibold text-gray-800">{producto.nombre}</h3>
                            <span class="text-lg font-bold text-green-600">${producto.precio}</span>
                        </div>
                        
                        <p class="text-gray-600 text-sm mb-3 line-clamp-2">{producto.descrip}</p>
                        
                        <div class="mb-3">
                            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">{$t('categories.categories')}:</span>
                            <p class="text-sm text-gray-700 mt-1">{obtenerCategoriasProducto(producto)}</p>
                        </div>
                        
                        {#if producto.firstImage}
                            <div class="mb-3">
                                <img 
                                    src={producto.firstImage.url} 
                                    alt={producto.nombre}
                                    class="w-full h-32 object-cover rounded-md"
                                />
                            </div>
                        {/if}
                        
                        <div class="flex justify-between items-center">
                            <span class="text-xs text-gray-500">
                                {$t('common.status')}: {producto.sta ? $t('categories.active') : $t('categories.inactive')}
                            </span>
                            {#if producto.parent}
                                <span class="text-xs text-blue-600">
                                    Subproducto de: {producto.parent.nombre}
                                </span>
                            {/if}
                        </div>
                    </div>
                {/each}
            </div>
        {/if}
    </div>
</div>

<style>
    .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
</style>
